import { useEffect, useRef } from 'react';
import { useSocket } from '@/contexts/SocketContext';
import type { ChannelTaskCreatedEvent, ChannelTaskUpdatedEvent } from '../types/tracker.types';

interface TrackerComCreatedEvent {
  channelId: string;
  affaire_id: number;
  tracker_com: any;
  author: { id: string; name: string };
  timestamp: string;
}

interface TrackerComUpdatedEvent {
  channelId: string;
  affaire_id: number;
  tracker_com_id: string;
  is_done: boolean;
  done_by: { id: string; name: string };
  timestamp: string;
}

interface UseChannelNotificationsProps {
  channelId: string | null;
  onTaskCreated?: (event: ChannelTaskCreatedEvent) => void;
  onTaskUpdated?: (event: ChannelTaskUpdatedEvent) => void;
  onCommunicationCreated?: (event: TrackerComCreatedEvent) => void;
  onCommunicationUpdated?: (event: TrackerComUpdatedEvent) => void;
}

export const useChannelNotifications = ({
  channelId,
  onTaskCreated,
  onTaskUpdated,
  onCommunicationCreated,
  onCommunicationUpdated
}: UseChannelNotificationsProps) => {
  const { socket, isConnected } = useSocket();
  const subscribedChannelRef = useRef<string | null>(null);

  useEffect(() => {
    if (!socket || !channelId) return;

    // Subscribe to channel
    if (channelId !== subscribedChannelRef.current) {
      // Unsubscribe from previous channel
      if (subscribedChannelRef.current) {
        socket.emit('unsubscribe:channel', subscribedChannelRef.current);
        console.log(`[Tracker] Unsubscribed from channel: ${subscribedChannelRef.current}`);
      }

      // Subscribe to new channel
      socket.emit('subscribe:channel', channelId);
      subscribedChannelRef.current = channelId;
      console.log(`[Tracker] Subscribed to channel: ${channelId}`);
    }

    // Listen for channel events
    const handleTaskCreated = (event: ChannelTaskCreatedEvent) => {
      console.log('[Tracker] Task created event:', event);
      if (onTaskCreated) {
        onTaskCreated(event);
      }
    };

    const handleTaskUpdated = (event: ChannelTaskUpdatedEvent) => {
      console.log('[Tracker] Task updated event:', event);
      if (onTaskUpdated) {
        onTaskUpdated(event);
      }
    };

    const handleCommunicationCreated = (event: TrackerComCreatedEvent) => {
      console.log('[Tracker] Communication created event:', event);
      if (onCommunicationCreated) {
        onCommunicationCreated(event);
      }
    };

    const handleCommunicationUpdated = (event: TrackerComUpdatedEvent) => {
      console.log('[Tracker] Communication updated event:', event);
      if (onCommunicationUpdated) {
        onCommunicationUpdated(event);
      }
    };

    // Register event listeners
    socket.on('channel:task:created', handleTaskCreated);
    socket.on('channel:task:updated', handleTaskUpdated);
    socket.on('tracker:com:created', handleCommunicationCreated);
    socket.on('tracker:com:updated', handleCommunicationUpdated);

    // Cleanup
    return () => {
      socket.off('channel:task:created', handleTaskCreated);
      socket.off('channel:task:updated', handleTaskUpdated);
      socket.off('tracker:com:created', handleCommunicationCreated);
      socket.off('tracker:com:updated', handleCommunicationUpdated);
    };
  }, [socket, channelId, onTaskCreated, onTaskUpdated, onCommunicationCreated, onCommunicationUpdated]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (socket && subscribedChannelRef.current) {
        socket.emit('unsubscribe:channel', subscribedChannelRef.current);
        console.log(`[Tracker] Cleanup: Unsubscribed from channel: ${subscribedChannelRef.current}`);
        subscribedChannelRef.current = null;
      }
    };
  }, [socket]);

  return {
    isConnected
  };
};
